import { create } from 'zustand';
import Message from '@/types/messageType';
import headerObject from '@/types/headerObj';
import emptyHeaderObj from '@/types/emptyHeaderObj';
import useUserStore from './userState';
import useOrganizationStore from './orgState';
import { createJSONStorage, persist } from 'zustand/middleware';

const getChatStorageName = (): string => {
  const DEFAULT_CHAT_MESSAGES_STORAGE = 'chat-messages';
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    const [repoOwner = '', repoName = ''] = pathname.split('/')[1].split('&');
    return `chat-messages-${repoOwner}-${repoName}`;
  }
  return DEFAULT_CHAT_MESSAGES_STORAGE;
};

const headerObj: headerObject = emptyHeaderObj;
headerObj.Useruuid = useUserStore.getState().user?.UserUUID;
headerObj.Useremail = useUserStore.getState().user?.Email;
headerObj.Username = useUserStore.getState().user?.FirstName;
headerObj.Orgname = useOrganizationStore.getState().organization?.OrgName;
headerObj.Orguuid = useOrganizationStore.getState().organization?.OrgUUID;

const isUserLoggedIn = (): boolean => {
  return Boolean(useUserStore.getState().user?.UserUUID);
};

interface MessageState {
  messageList: Message[];
  numOfMessages: number;
  setMessageArr: (arg: Message) => void;
  setMessage: (arg: Message, removeLastItem?: boolean) => void;
  popMessage: () => void;
  clearMessageList: () => void;
  setNumOfMessages: (num: number) => void;
}

const useMessageStore = create<MessageState>()(
  persist(
    (set, get) => ({
      messageList: [],
      numOfMessages: 0,
      setNumOfMessages: (num: number) => {
        set({ numOfMessages: num });
      },
      setMessageArr: async (arg) => {
        set((state) => ({
          messageList: [
            ...state.messageList,
            {
              UserUUID: arg.UserUUID,
              ConversationUUID: arg.ConversationUUID,
              Text: arg.Text,
              TimeStamp: arg.TimeStamp,
              Actor: arg.Actor,
              UUID: arg.UUID,
            },
          ],
        }));
      },
      setMessage: (arg, removeLastItem = true) => {
        set((state) => {
          const newMessage = {
            UserUUID: arg.UserUUID,
            ConversationUUID: arg.ConversationUUID,
            Text: arg.Text,
            TimeStamp: arg.TimeStamp,
            Actor: arg.Actor,
            UUID: arg.UUID,
          };
          let messageList = [...state.messageList];
          if (removeLastItem) {
            messageList = messageList.slice(0, -1);
          }

          return {
            messageList: [...messageList, newMessage],
          };
        });
      },
      popMessage: () => {
        set((state) => ({
          ...state.messageList.pop(),
          messageList: [...state.messageList],
        }));
      },
      clearMessageList: () => {
        set({
          messageList: [],
        });
      },
    }),
    {
      name: getChatStorageName(),
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (isUserLoggedIn()) {
          sessionStorage.removeItem(getChatStorageName());
          return null;
        }
        return state;
      },
      partialize: (state) => {
        if (isUserLoggedIn()) {
          return {};
        }
        return state;
      },
    }
  )
);

export default useMessageStore;
